import { useEffect, useState } from "react";
import { EditIcon, PauseIcon, PlayIcon } from "./icons";
import type { AuthUser } from "../types/auth";

interface PriceAlertSubscription {
  categories: string[];
  chips?: string[];
  created_at: string;
  enabled: boolean;
  id: string;
  memories?: string[];
  models?: string[];
  name: string;
  notification_count: number;
  paused: boolean;
  storages?: string[];
  target_price: number;
}

interface FilterOptions {
  chips: string[];
  memories: string[];
  models: string[];
  storages: string[];
}

interface PriceAlertSectionProps {
  authUser: AuthUser | null;
  categories: string[];
}

const optionClass = (selected: boolean) =>
  `px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
    selected
      ? "bg-[#0071E3] text-white"
      : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
  }`;

export default function PriceAlertSection({
  authUser,
  categories,
}: PriceAlertSectionProps) {
  const [subscriptions, setSubscriptions] = useState<PriceAlertSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    chips: [],
    storages: [],
    memories: [],
    models: [],
  });

  const [name, setName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [selectedStorages, setSelectedStorages] = useState<string[]>([]);
  const [selectedMemories, setSelectedMemories] = useState<string[]>([]);
  const [targetPrice, setTargetPrice] = useState("");

  useEffect(() => {
    if (!authUser) {
      setSubscriptions([]);
      resetForm();
      return;
    }
    const loadSubscriptions = async () => {
      try {
        const res = await fetch("/api/price-alert-subscriptions");
        const data = await res.json();
        setSubscriptions(data.subscriptions || []);
      } catch (error) {
        console.error("Failed to fetch price alert subscriptions:", error);
      }
    };
    void loadSubscriptions();
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      setFilterOptions({ chips: [], storages: [], memories: [], models: [] });
      return;
    }
    void fetchFilterOptions(selectedCategory);
  }, [selectedCategory, authUser]);

  const fetchSubscriptions = async () => {
    if (!authUser) {
      setSubscriptions([]);
      return;
    }

    try {
      const res = await fetch("/api/price-alert-subscriptions");
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (error) {
      console.error("Failed to fetch price alert subscriptions:", error);
    }
  };

  const fetchFilterOptions = async (category: string) => {
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      const res = await fetch(`/api/filter-options?${params.toString()}`);
      const data = await res.json();
      setFilterOptions({
        chips: data.chips || [],
        storages: data.storages || [],
        memories: data.memories || [],
        models: data.models || [],
      });
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
      setFilterOptions({ chips: [], storages: [], memories: [], models: [] });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setSelectedCategory("");
    setSelectedModels([]);
    setSelectedChips([]);
    setSelectedStorages([]);
    setSelectedMemories([]);
    setTargetPrice("");
  };

  const toggleValue = (
    value: string,
    values: string[],
    setter: (nextValues: string[]) => void,
  ) => {
    setter(
      values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value],
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!authUser) {
      alert("请先登录");
      return;
    }

    if (!authUser.has_bark_key) {
      alert("请先在账号中配置 Bark Key");
      return;
    }

    const numericTarget = Number(targetPrice);
    if (!numericTarget || numericTarget <= 0) {
      alert("请输入有效的目标价格");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        categories: selectedCategory ? [selectedCategory] : [],
        models: selectedModels,
        chips: selectedChips,
        storages: selectedStorages,
        memories: selectedMemories,
        target_price: numericTarget,
        enabled: true,
      };

      const url = editingId
        ? `/api/price-alert-subscriptions/${editingId}`
        : "/api/price-alert-subscriptions";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || "保存失败");
        return;
      }

      resetForm();
      await fetchSubscriptions();
    } catch (error) {
      console.error("Failed to save price alert subscription:", error);
      alert("保存失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("确定要删除这个价格通知吗？")) return;
    try {
      await fetch(`/api/price-alert-subscriptions/${id}`, { method: "DELETE" });
      await fetchSubscriptions();
      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      console.error("Failed to delete price alert subscription:", error);
    }
  };

  const handlePauseResume = async (id: string, paused: boolean) => {
    try {
      const endpoint = paused ? "/pause" : "/resume";
      await fetch(`/api/price-alert-subscriptions/${id}${endpoint}`, {
        method: "PATCH",
      });
      await fetchSubscriptions();
    } catch (error) {
      console.error("Failed to pause/resume price alert subscription:", error);
    }
  };

  const startEdit = (sub: PriceAlertSubscription) => {
    setEditingId(sub.id);
    setName(sub.name);
    setSelectedCategory(sub.categories?.[0] || "");
    setSelectedModels(sub.models || []);
    setSelectedChips(sub.chips || []);
    setSelectedStorages(sub.storages || []);
    setSelectedMemories(sub.memories || []);
    setTargetPrice(String(sub.target_price || ""));
  };

  return (
    <div className="mt-6 border-t border-gray-100 pt-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-[#1D1D1F]">价格通知</h3>
        <p className="text-xs text-gray-500 mt-1">
          命中“低于目标价”后会通过 Bark 推送 5 次，时间间隔为 0 / 1 / 5 / 10 / 30 分钟。
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-2xl">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold text-[#1D1D1F]">
            {editingId ? "编辑价格通知" : "添加价格通知"}
          </h4>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              取消编辑
            </button>
          )}
        </div>

        <div className="mb-3">
          <label className="block text-xs text-gray-500 mb-1">
            名称 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="例如: MacBook Pro M5 低于 8999"
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
            required
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs text-gray-500 mb-1">产品类型</label>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => {
                  setSelectedCategory(selectedCategory === category ? "" : category);
                  setSelectedModels([]);
                }}
                className={optionClass(selectedCategory === category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {filterOptions.models.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">产品型号</label>
            <div className="flex flex-wrap gap-2">
              {filterOptions.models.map((model) => (
                <button
                  key={model}
                  type="button"
                  onClick={() => toggleValue(model, selectedModels, setSelectedModels)}
                  className={optionClass(selectedModels.includes(model))}
                >
                  {model}
                </button>
              ))}
            </div>
          </div>
        )}

        {filterOptions.chips.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">芯片型号</label>
            <div className="flex flex-wrap gap-2">
              {filterOptions.chips.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => toggleValue(chip, selectedChips, setSelectedChips)}
                  className={optionClass(selectedChips.includes(chip))}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {filterOptions.memories.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">内存</label>
            <div className="flex flex-wrap gap-2">
              {filterOptions.memories.map((memory) => (
                <button
                  key={memory}
                  type="button"
                  onClick={() => toggleValue(memory, selectedMemories, setSelectedMemories)}
                  className={optionClass(selectedMemories.includes(memory))}
                >
                  {memory}
                </button>
              ))}
            </div>
          </div>
        )}

        {filterOptions.storages.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">存储</label>
            <div className="flex flex-wrap gap-2">
              {filterOptions.storages.map((storage) => (
                <button
                  key={storage}
                  type="button"
                  onClick={() => toggleValue(storage, selectedStorages, setSelectedStorages)}
                  className={optionClass(selectedStorages.includes(storage))}
                >
                  {storage}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">
            目标价格 <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={targetPrice}
            onChange={(event) => setTargetPrice(event.target.value)}
            placeholder="例如 8999"
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading || !authUser || !authUser.has_bark_key}
          className="w-full py-2.5 bg-[#0071E3] text-white rounded-xl font-semibold hover:bg-[#0077ED] transition-colors disabled:opacity-50"
        >
          {loading
            ? "保存中..."
            : editingId
              ? "更新价格通知"
              : "保存价格通知"}
        </button>
      </form>

      <div>
        <h4 className="text-sm font-semibold text-[#1D1D1F] mb-3">
          已设置的价格通知
        </h4>
        {!authUser ? (
          <p className="text-sm text-gray-400 text-center py-4">
            请先登录查看您的订阅
          </p>
        ) : subscriptions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            暂无价格通知
          </p>
        ) : (
          <div className="space-y-2">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className={`p-3 rounded-xl ${sub.paused ? "bg-gray-100 opacity-75" : "bg-gray-50"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1D1D1F]">
                        {sub.name}
                      </span>
                      {sub.paused && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-700">
                          已暂停
                        </span>
                      )}
                      {sub.notification_count > 0 && (
                        <span className="text-xs text-gray-400">
                          已通知 {sub.notification_count} 次
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {sub.categories.length > 0
                        ? sub.categories.join(", ")
                        : "全部分类"}
                      {sub.models && sub.models.length > 0
                        ? ` · 型号: ${sub.models.join(", ")}`
                        : ""}
                      {sub.chips && sub.chips.length > 0
                        ? ` · 芯片: ${sub.chips.join(", ")}`
                        : ""}
                      {sub.memories && sub.memories.length > 0
                        ? ` · 内存: ${sub.memories.join(", ")}`
                        : ""}
                      {sub.storages && sub.storages.length > 0
                        ? ` · 存储: ${sub.storages.join(", ")}`
                        : ""}
                      {` · 低于 ¥${sub.target_price}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePauseResume(sub.id, !sub.paused)}
                      className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title={sub.paused ? "恢复通知" : "暂停通知"}
                    >
                      {sub.paused ? (
                        <PlayIcon className="w-4 h-4" />
                      ) : (
                        <PauseIcon className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => startEdit(sub)}
                      className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="编辑"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(sub.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="删除"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
